import React from "react";
import "./DeclinedApprovedModal.css";

const DeclinedApprovedModal = ({
  isOpen,
  onClose,
  modalType,
  onProceed,
  onDismiss,
  onTryAgain,
  actionLoading = false,
}) => {
  if (!isOpen) return null;

  const failedIcon = (
    <div className="dc-modal-icon dc-shield-x">
      <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M50 10L85 25V50C85 72 70 85 50 90C30 85 15 72 15 50V25L50 10Z"
          fill="#E53935"
        />
        <path
          d="M35 35L65 65M35 65L65 35"
          stroke="white"
          strokeWidth="8"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );

  const icons = {
    approveConfirmation: (
      <div className="dc-modal-icon dc-check-circle">
        <svg viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="40" fill="#4CAF50" />
          <path
            d="M35 50L45 60L65 40"
            stroke="white"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    ),
    declineConfirmation: (
      <div className="dc-modal-icon dc-recycle">
        <svg
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M35 20L40 35H25L35 20Z" fill="#4CAF50" />
          <path d="M65 20L75 35H60L65 20Z" fill="#4CAF50" />
          <path d="M50 70L40 55H60L50 70Z" fill="#4CAF50" />
          <path
            d="M35 20L40 35L25 35L35 20ZM65 20L75 35L60 35L65 20ZM50 70L40 55L60 55L50 70ZM35 20L65 20L50 70L35 20Z"
            stroke="#4CAF50"
            strokeWidth="4"
          />
        </svg>
      </div>
    ),
    declined: (
      <div className="dc-modal-icon dc-check-circle">
        <svg
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="50" cy="50" r="40" fill="#4CAF50" />
          <path
            d="M35 50L45 60L65 40"
            stroke="white"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    ),
    failed: failedIcon,
    approved: (
      <div className="dc-modal-icon dc-check-circle">
        <svg
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="50" cy="50" r="40" fill="#4CAF50" />
          <path
            d="M35 50L45 60L65 40"
            stroke="white"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    ),
    cancelRefundConfirmation: (
      <div className="dc-modal-icon dc-recycle">
        <svg viewBox="0 0 100 100" fill="none">
          <path d="M35 20L40 35H25L35 20Z" fill="#FF9800" />
          <path d="M65 20L75 35H60L65 20Z" fill="#FF9800" />
          <path d="M50 70L40 55H60L50 70Z" fill="#FF9800" />
          <path
            d="M35 20L40 35L25 35L35 20ZM65 20L75 35L60 35L65 20ZM50 70L40 55L60 55L50 70ZM35 20L65 20L50 70L35 20Z"
            stroke="#FF9800"
            strokeWidth="4"
          />
        </svg>
      </div>
    ),
    cancelled: (
      <div className="dc-modal-icon dc-check-circle">
        <svg viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="40" fill="#4CAF50" />
          <path
            d="M35 50L45 60L65 40"
            stroke="white"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    ),
    failedCancel: failedIcon,
  };

  const proceedLabel =
    modalType === "approveConfirmation"
      ? "Approve"
      : modalType === "declineConfirmation"
      ? "Decline"
      : modalType === "cancelRefundConfirmation"
      ? "Cancel Refund"
      : "Proceed";

  const lockModal = [
    "approved",
    "declined",
    "cancelled",
    "approveConfirmation",
    "declineConfirmation",
    "cancelRefundConfirmation",
  ].includes(modalType);

  const modalContent = {
    approveConfirmation: {
      title: "Approve Confirmation",
      message: "Are you sure you want to approve this refund?",
      icon: icons.approveConfirmation,
      buttons: (
        <>
          <button
            className="dc-modal-button dc-button-secondary"
            onClick={onClose}
            disabled={actionLoading}
          >
            Cancel
          </button>
          <button
            className="dc-modal-button dc-button-primary"
            onClick={onProceed}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <span className="dc-declined-spinner" />
            ) : (
              proceedLabel
            )}
          </button>
        </>
      ),
    },
    declineConfirmation: {
      title: "Decline Confirmation",
      message: "Are you sure you want to decline this request?",
      icon: icons.declineConfirmation,
      buttons: (
        <>
          <button
            className="dc-modal-button dc-button-secondary"
            onClick={onClose}
            disabled={actionLoading}
          >
            Close
          </button>
          <button
            className="dc-modal-button dc-button-primary"
            onClick={onProceed}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <span className="dc-declined-spinner" />
            ) : (
              proceedLabel
            )}
          </button>
        </>
      ),
    },
    declined: {
      title: "Declined",
      message: "Refund to this user has been declined.",
      icon: icons.declined,
      buttons: (
        <button
          className="dc-modal-button dc-button-primary dc-full-width"
          onClick={onDismiss}
        >
          Dismiss
        </button>
      ),
    },
    failed: {
      title: "Failed",
      message: "Refund decline to this account failed, try again.",
      icon: icons.failed,
      buttons: (
        <>
          <button
            className="dc-modal-button dc-button-secondary"
            onClick={onClose}
          >
            Close
          </button>
          <button
            className="dc-modal-button dc-button-primary"
            onClick={onTryAgain}
          >
            Try Again
          </button>
        </>
      ),
    },
    approved: {
      title: "Approved",
      message: "Refund to this user has been successfully approved.",
      icon: icons.approved,
      buttons: (
        <button
          className="dc-modal-button dc-button-primary dc-full-width"
          onClick={onDismiss}
          disabled={actionLoading}
        >
          Dismiss
        </button>
      ),
    },
    failedApproval: {
      title: "Failed",
      message: "Refund approval to this account failed, try again.",
      icon: icons.failed,
      buttons: (
        <>
          <button
            className="dc-modal-button dc-button-secondary"
            onClick={onClose}
            disabled={actionLoading}
          >
            Close
          </button>
          <button
            className="dc-modal-button dc-button-primary"
            onClick={onTryAgain}
            disabled={actionLoading}
          >
            Try Again
          </button>
        </>
      ),
    },
    cancelRefundConfirmation: {
      title: "Cancel Refund Confirmation",
      message: "Are you sure you want to cancel this refund request?",
      icon: icons.cancelRefundConfirmation,
      buttons: (
        <>
          <button
            className="dc-modal-button dc-button-secondary"
            onClick={onClose}
            disabled={actionLoading}
          >
            Close
          </button>
          <button
            className="dc-modal-button dc-button-primary"
            onClick={onProceed}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <span className="dc-declined-spinner" />
            ) : (
              proceedLabel
            )}
          </button>
        </>
      ),
    },
    cancelled: {
      title: "Cancelled",
      message: "Refund request has been successfully cancelled.",
      icon: icons.cancelled,
      buttons: (
        <button
          className="dc-modal-button dc-button-primary dc-full-width"
          onClick={onDismiss}
        >
          Dismiss
        </button>
      ),
    },
    failedCancel: {
      title: "Failed",
      message: "Failed to cancel refund. Please try again.",
      icon: icons.failedCancel,
      buttons: (
        <>
          <button
            className="dc-modal-button dc-button-secondary"
            onClick={onClose}
          >
            Close
          </button>
          <button
            className="dc-modal-button dc-button-primary"
            onClick={onTryAgain}
          >
            Try Again
          </button>
        </>
      ),
    },
  };

  const content = modalContent[modalType] || modalContent.declineConfirmation;

  return (
    <div className="dc-modal-overlay" onClick={lockModal ? undefined : onClose}>
      <div className="dc-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="dc-modal-close" onClick={onClose}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="dc-modal-content">
          {content.icon}
          <h2 className="dc-modal-title">{content.title}</h2>
          <p className="dc-modal-message">{content.message}</p>
          <div className="dc-modal-buttons">{content.buttons}</div>
        </div>
      </div>
    </div>
  );
};

export default DeclinedApprovedModal;
